
import { ref, Ref } from "vue";
import { Component, Vue, Watch, Prop } from "vue-property-decorator";

import { IRouteMeta } from "@/router/interface";
import program_routes from "@/api/routes/programs";

import { Debounce } from "@/helpers";

import CInfiniteScroll from "@/components/InfiniteScroll.vue";

import StructureFactory from "@/models/structure/factory";

export interface IProgramAssignmentAvailablePosition {
  id: string;
  name: string;
  factory_id: string;
  factory_full_name: string;
}

@Component({
  name: "CProgramAssignmentsEditPositions",
  components: {
    CInfiniteScroll
  }
})
export default class CProgramAssignmentsEditPositions extends Vue {
  @Prop({ required: true }) public position_ids!: string[];
  @Prop({ required: true }) public factories!: StructureFactory[];

  public selected_factory: StructureFactory | null = null;

  private positions_is_loading: boolean = false;
  private positions_current_page: number = 0;
  private positions_next_page: boolean = true;
  private positions_per_page: number = 100;
  public positions_search: string = "";
  public available_positions: IProgramAssignmentAvailablePosition[] = [];
  public selected_positions: Ref<Set<string>> = ref(new Set<string>());

  public show_filter: boolean = false;
  public filters_count: number = 0;
  public filters: Map<string, boolean> = new Map();

  protected async created() {
    this.selected_positions.value = new Set(this.position_ids);

    await this.clearPositions();
  }

  private async clearPositions() {
    this.available_positions = [];

    await this.loadPositions();
  }

  public async clearFilters() {
    this.filters.clear();

    this.selected_factory = null;

    this.filters_count = this.filters.size;

    await this.clearPositions();
  }

  public async changeFactoryFilter() {
    if (this.selected_factory) {
      this.filters.set("factory", true);
    } else {
      this.filters.delete("factory");
    }

    this.filters_count = this.filters.size;

    await this.clearPositions();
  }

  @Watch("positions_search")
  @Debounce(500)
  protected async searchPositions() {
    await this.clearPositions();
  }

  public async positionsInfiniteHandler() {
    if (this.positions_next_page && !this.positions_is_loading) {
      await this.loadPositions(this.positions_current_page + 1);
    }
  }

  public changeSelectedPositions(position_id: string) {
    if (this.selected_positions.value.has(position_id)) {
      this.selected_positions.value.delete(position_id);
    } else {
      this.selected_positions.value.add(position_id);
    }

    this.$emit("change", Array.from(this.selected_positions.value));
  }

  private async loadPositions(page: number = 0) {
    this.positions_is_loading = true;

    return this.$api
      .get(program_routes.assignment_available_positions, {
        params: {
          page,
          per_page: this.positions_per_page,
          search: this.positions_search,
          factory_id: this.selected_factory?.id,
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(
        ({ data: res }: { data: IProgramAssignmentAvailablePosition[] }) => {
          this.available_positions.push(...res);

          this.positions_current_page = page;
          this.positions_next_page = res.length === this.positions_per_page;
        }
      )
      .finally(() => {
        this.positions_is_loading = false;
      });
  }

  public allowWrite() {
    return this.$api.allowWrite();
  }
}
